import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check, Pen, RefreshCw, MessageCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { fixMalformedReferences } from '@/features/chat/shared';
import { LoadingDots, ChatMessage } from '@/features/chat/shared/components';
import { useAutoScroll } from '@/features/chat/shared/hooks/useAutoScroll';
import { useMarkdownComponents } from '@/features/chat/shared/hooks/useMarkdownComponents';
import { stripReferences } from '@/features/chat/shared/utils/clipboard';

import { FileType } from './types';

interface ChatMessagesProps {
  completion: string;
  isLoading: boolean;
  stage: string;
  loadingStage: string | null;
  files: {
    data: FileType[];
  };
  hideReferences: boolean;
  resultSearchTerm?: string;
  setResultSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  onCopyContent?: (content: string) => void;
  copySuccess?: boolean;
  onClearCompletion?: () => void;
  onEditQuery?: (messageIndex: number, newContent: string) => void;
  onRegenerateResponse?: (messageIndex: number) => void;
  conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
  hideToolbar?: boolean;
  isFileDragging?: boolean;
  hasFiles?: boolean;
  hasInputText?: boolean;
  onAddFilesClick?: () => void;
  isLoadingFromHistory?: boolean;
}

const ChatMessages: React.FC<ChatMessagesProps> = ({
  completion,
  isLoading,
  stage,
  files,
  hideReferences,
  resultSearchTerm: externalResultSearchTerm,
  onEditQuery,
  onRegenerateResponse,
  conversationHistory = [],
  isFileDragging = false,
  hasFiles = false,
  hasInputText = false,
  onAddFilesClick,
  isLoadingFromHistory = false,
}) => {
  const [internalResultSearchTerm] = useState('');
  const [copiedMessageIndex, setCopiedMessageIndex] = useState<number | null>(
    null,
  );
  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null,
  );
  const [showLoadingDots, setShowLoadingDots] = useState(false);

  // Track the last conversation length to detect when a new message is added from streaming
  const previousConversationLengthRef = React.useRef(
    conversationHistory.length,
  );
  const [justAddedMessageIndex, setJustAddedMessageIndex] = useState<
    number | null
  >(null);

  // Track the conversation length when loading from history started
  // Messages that existed when isLoadingFromHistory became true should never animate
  const loadingFromHistoryLengthRef = React.useRef(0);

  // When isLoadingFromHistory becomes true, capture the current conversation length
  // All messages up to this index should never animate
  React.useEffect(() => {
    if (isLoadingFromHistory) {
      loadingFromHistoryLengthRef.current = conversationHistory.length;
    }
  }, [isLoadingFromHistory, conversationHistory.length]);

  // Detect when a new assistant message is added (from streaming completion)
  React.useEffect(() => {
    const currentLength = conversationHistory.length;
    const previousLength = previousConversationLengthRef.current;

    // Check if a new message was added and it's an assistant message
    if (currentLength > previousLength) {
      const newMessageIndex = currentLength - 1;
      const newMessage = conversationHistory[newMessageIndex];

      // Only skip animation if it's an assistant message (which came from streaming)
      if (newMessage?.role === 'assistant') {
        setJustAddedMessageIndex(newMessageIndex);
        // Clear the flag after a short delay to allow future animations
        setTimeout(() => setJustAddedMessageIndex(null), 100);
      }
    }

    previousConversationLengthRef.current = currentLength;
  }, [conversationHistory]);

  // Control loading dots visibility with delay for first message animation
  React.useEffect(() => {
    if (isLoading && stage === 'processing') {
      // Check if this is the first message (conversation has exactly 1 user message)
      const isFirstMessage = conversationHistory.length === 1;

      if (isFirstMessage) {
        // Delay showing loading dots for first message entrance animation (600ms)
        const timer = setTimeout(() => setShowLoadingDots(true), 650);
        return () => clearTimeout(timer);
      }
      // Show immediately for subsequent messages
      setShowLoadingDots(true);
      return undefined;
    }
    // Hide loading dots when not in processing stage
    setShowLoadingDots(false);
    return undefined;
  }, [isLoading, stage, conversationHistory.length]);

  // Use shared auto-scroll hook
  const { containerRef, scrollAnchorRef } = useAutoScroll({
    dependencies: [conversationHistory.length, completion],
    enabled: true,
    isStreaming: isLoading && stage === 'final',
  });

  // Use external search term if provided, otherwise use internal
  const resultSearchTerm = externalResultSearchTerm ?? internalResultSearchTerm;

  // Use shared markdown components hook with file reference support
  const markdownComponents = useMarkdownComponents({
    files,
    hideReferences,
    searchTerm: resultSearchTerm,
  });

  const isEmpty =
    !completion &&
    !isLoading &&
    stage !== 'processing' &&
    conversationHistory.length === 0;

  return (
    <div className="overflow-hidden flex flex-col">
      <div
        className={cn(
          'flex-1 overflow-y-auto overflow-x-hidden',
          isEmpty ? 'flex items-center justify-center' : 'flex flex-col',
        )}
        role="log"
        ref={containerRef}
      >
        {isEmpty ? (
          <div className="text-center p-8">
            {(() => {
              if (isFileDragging) {
                return (
                  <p className="text-sm text-muted-foreground">
                    Drop to add context
                  </p>
                );
              }
              if (!hasFiles && hasInputText) {
                return (
                  <button
                    type="button"
                    onClick={onAddFilesClick}
                    className="text-lg text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                  >
                    Click to add files
                  </button>
                );
              }
              return (
                <MessageCircle className="w-8 h-8 text-muted-foreground/30 mx-auto" />
              );
            })()}
          </div>
        ) : (
          <>
            <div className="px-3 pt-2">
              <div className="max-w-3xl mx-auto">
                {/* Conversation Messages */}
                <div className="space-y-0 smooth-scroll">
                  {conversationHistory.map((message, index) => {
                    const isEditing = editingMessageIndex === index;
                    // Check if this is the very first user message in the conversation
                    const isFirstUserMessage =
                      index === 0 && message.role === 'user';
                    // Check if this message was just added from streaming (to skip animation)
                    const wasJustAddedFromStream =
                      index === justAddedMessageIndex;

                    // Build actions based on role
                    const actions =
                      message.role === 'user'
                        ? [
                            {
                              icon: Pen,
                              onClick: () => setEditingMessageIndex(index),
                              title: 'Edit message',
                            },
                          ]
                        : [
                            {
                              icon: Copy,
                              onClick: () => {
                                // Strip references before copying
                                const cleanContent = stripReferences(
                                  message.content,
                                );
                                navigator.clipboard.writeText(cleanContent);
                                setCopiedMessageIndex(index);
                                setTimeout(
                                  () => setCopiedMessageIndex(null),
                                  2000,
                                );
                              },
                              title: 'Copy message',
                              activeIcon: Check,
                              isActive: copiedMessageIndex === index,
                              activeColor: 'text-green-600',
                            },
                            {
                              icon: RefreshCw,
                              onClick: () => onRegenerateResponse?.(index),
                              title: 'Regenerate response',
                            },
                          ];

                    // Determine animation class
                    let animationClass = '';
                    // Never animate assistant messages (they come from streaming and should stay in place)
                    // Only animate user messages that weren't loaded from history
                    // Check if this message existed when we started loading from history
                    const wasLoadedFromHistory =
                      index < loadingFromHistoryLengthRef.current;

                    if (
                      message.role === 'user' &&
                      !wasJustAddedFromStream &&
                      !wasLoadedFromHistory
                    ) {
                      if (isFirstUserMessage) {
                        animationClass = 'animate-first-message-entrance';
                      } else {
                        animationClass = 'animate-fade-in-up';
                      }
                    }

                    return (
                      <ChatMessage
                        key={index}
                        role={message.role}
                        content={
                          message.role === 'assistant'
                            ? fixMalformedReferences(message.content)
                            : message.content
                        }
                        actions={actions}
                        isEditing={isEditing}
                        onEdit={(newContent) => {
                          onEditQuery?.(index, newContent);
                          setEditingMessageIndex(null);
                        }}
                        onCancelEdit={() => setEditingMessageIndex(null)}
                        markdownComponents={
                          message.role === 'assistant'
                            ? markdownComponents
                            : undefined
                        }
                        searchTerm={resultSearchTerm}
                        className={animationClass}
                      />
                    );
                  })}

                  {/* Show loading indicator when still loading but no content yet */}
                  <Collapsible
                    open={
                      showLoadingDots && isLoading && stage === 'processing'
                    }
                  >
                    <CollapsibleContent className="overflow-hidden">
                      <div className="flex items-start gap-1 py-2 w-full">
                        <div className="flex flex-col items-start">
                          <div className="relative px-2 py-0 w-fit mb-0.5">
                            <LoadingDots className="py-2" />
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* Streaming message - shows completion as it streams */}
                  {/* Only show streaming message if it's not already in conversation history */}
                  {completion &&
                    stage === 'final' &&
                    isLoading &&
                    conversationHistory[conversationHistory.length - 1]
                      ?.role !== 'assistant' && (
                      <div className="flex items-start gap-1 py-1 w-full group">
                        <div className="flex flex-col items-start">
                          <div className="relative w-fit mb-0.5 px-2 py-0">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={markdownComponents}
                            >
                              {fixMalformedReferences(completion)}
                            </ReactMarkdown>
                          </div>
                          {/* Invisible placeholder for action buttons to prevent layout shift */}
                          <div className="flex items-center gap-1 mt-0.5 ml-2 opacity-0 pointer-events-none">
                            <div className="p-1 w-[28px] h-[28px]" />
                            <div className="p-1 w-[28px] h-[28px]" />
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
            <div ref={scrollAnchorRef} />
          </>
        )}
      </div>
    </div>
  );
};

export default ChatMessages;
